/**
 * Forward geocoder — city/state/country → {lat, lon}.
 *
 * REUSE, not reinvent: `geocode()` is the SAME OpenStreetMap Nominatim lookup
 * the astrology app uses (av-astro/frontend/src/api/client.ts) and that powered
 * the legacy Vastu-intake person geocode (src/_legacy/lib/geocoder.ts) — kept
 * for the single structured save-time lookup. The live typeahead `geocodeSearch`
 * moved to Photon (photon.komoot.io, same OSM data) because Nominatim cannot do
 * the prefix / autocomplete matching the city picker needs — see its docblock.
 * Promoted out of _legacy so the live L1A intake can auto-fill place-of-birth
 * coordinates from City + State + Country.
 *
 * Two endpoints, two roles: Photon = the live typeahead search (`geocodeSearch`),
 * Nominatim = the single structured save-time lookup (`geocode`).
 *
 *   - 1 req/sec rate limit enforced client-side on the Nominatim `geocode()`
 *     lookup only (Nominatim usage policy); Photon has its own fair-use throttle.
 *   - Both endpoints abort a hung request after 8s and degrade gracefully
 *     (empty results / null) rather than hanging.
 *   - Results cached in Firestore at `geocache/{slug}` so repeat lookups are
 *     instant (and offline-OK once cached).
 *
 * The API needs no key (free tier); nothing secret touches the browser.
 */

import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';

const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search';
const MIN_INTERVAL_MS = 1100; // 1 req/sec + safety
/** Abort any outbound geocode fetch (Photon or Nominatim) after this long so a
 *  hung endpoint degrades to empty/null instead of freezing the UI. */
const FETCH_TIMEOUT_MS = 8000;
let lastCallAt = 0;

/**
 * Photon (photon.komoot.io) — OSM-data geocoder built for TYPE-AHEAD / prefix
 * search, which plain Nominatim `/search` cannot do: Nominatim treats `q=rai`
 * as a whole token and only returns places literally NAMED "Rai" (foreign
 * hamlets), never prefix matches like Raipur/Raigarh. Photon indexes the same
 * OSM/Nominatim data but returns starts-with matches — the behaviour the owner
 * wants. Free, no key. Used ONLY by the live `geocodeSearch` autocomplete;
 * `geocode()` below stays on Nominatim (single structured save-time lookup).
 */
const PHOTON_URL = 'https://photon.komoot.io/api/';
/** India bounding box [minLon,minLat,maxLon,maxLat] — biases (not restricts)
 *  results toward India, whose importance ranking then surfaces major cities. */
const INDIA_BBOX = '68,6,97.5,37.5';
let lastPhotonAt = 0;
const PHOTON_MIN_INTERVAL_MS = 300; // komoot fair-use throttle (no hard 1/s rule)

/** OSM place types we treat as selectable settlements, ranked coarse→fine.
 *  Lower = more city-like → ranked higher. Anything not here is dropped. */
const SETTLEMENT_RANK: Record<string, number> = {
  city: 0,
  town: 1,
  municipality: 1,
  suburb: 2,
  village: 3,
  hamlet: 4,
  locality: 5,
};

export interface GeocodeResult {
  lat: number;
  lon: number;
  display_name: string;
  /** 'cache' when served from Firestore; 'nominatim' on fresh fetch. */
  source: 'cache' | 'nominatim';
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 120);
}

async function readCache(slug: string): Promise<GeocodeResult | null> {
  try {
    const snap = await getDoc(doc(db, 'geocache', slug));
    if (!snap.exists()) return null;
    const d = snap.data() as { lat?: number; lon?: number; display_name?: string };
    if (typeof d.lat !== 'number' || typeof d.lon !== 'number') return null;
    return { lat: d.lat, lon: d.lon, display_name: d.display_name || '', source: 'cache' };
  } catch {
    return null;
  }
}

async function writeCache(slug: string, r: GeocodeResult): Promise<void> {
  try {
    await setDoc(
      doc(db, 'geocache', slug),
      {
        lat: r.lat,
        lon: r.lon,
        display_name: r.display_name,
        queriedAt: serverTimestamp(),
        source: 'nominatim',
      },
      { merge: true },
    );
  } catch (err) {
    console.warn('[geocoder] cache write failed:', (err as Error).message);
  }
}

async function rateLimit(): Promise<void> {
  const now = Date.now();
  const wait = lastCallAt + MIN_INTERVAL_MS - now;
  if (wait > 0) await new Promise((r) => setTimeout(r, wait));
  lastCallAt = Date.now();
}

export interface GeocodeQuery {
  place: string; // city/town
  state?: string;
  country?: string; // human-readable, defaults to 'India'
  country_code?: string; // ISO-2 — only sent when known (over-constrains otherwise)
}

/**
 * Geocode a place name from a structured query (city + optional state + country).
 * Returns `null` if no result. Cache hits are instant.
 */
export async function geocode(input: string | GeocodeQuery): Promise<GeocodeResult | null> {
  const q: GeocodeQuery = typeof input === 'string' ? { place: input } : input;
  const place = (q.place || '').trim();
  if (!place) return null;

  const country = (q.country || '').trim();
  const country_code = (q.country_code || '').trim().toLowerCase();
  const slug = slugify(
    `${country_code || slugify(country) || 'xx'}__${slugify(q.state || '')}__${slugify(place)}`,
  );
  if (!slug) return null;

  // 1. Cache lookup
  const cached = await readCache(slug);
  if (cached) return cached;

  // 2. Live Nominatim hit (rate-limited)
  await rateLimit();
  const params = new URLSearchParams();
  params.set('format', 'json');
  params.set('limit', '1');
  params.set('city', place);
  if (q.state) params.set('state', q.state);
  if (country) params.set('country', country);
  if (country_code) params.set('countrycodes', country_code);
  const url = `${NOMINATIM_URL}?${params.toString()}`;
  try {
    const res = await fetch(url, {
      headers: {
        // Nominatim requires a UA/Referer identifying the app. Browser fetch
        // can't set User-Agent; the Referer (auto-set by the browser) plus an
        // explicit 'X-Requested-With' is the standard workaround.
        'X-Requested-With': 'AVBP-Portal',
        Accept: 'application/json',
      },
      // Abort a hung request after 8s so a save-time lookup degrades to null
      // (its normal no-result path) instead of hanging forever.
      signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
    });
    if (!res.ok) {
      console.warn('[geocoder] HTTP', res.status);
      return null;
    }
    const data = (await res.json()) as Array<{ lat: string; lon: string; display_name: string }>;
    if (!data || data.length === 0) return null;
    const top = data[0];
    const result: GeocodeResult = {
      lat: parseFloat(top.lat),
      lon: parseFloat(top.lon),
      display_name: top.display_name,
      source: 'nominatim',
    };
    if (!isFinite(result.lat) || !isFinite(result.lon)) return null;
    void writeCache(slug, result); // fire-and-forget cache write
    return result;
  } catch (err) {
    console.warn('[geocoder] fetch failed:', (err as Error).message);
    return null;
  }
}

/**
 * A single candidate place from a live city search — carries STRUCTURED
 * city / state / country (via Nominatim `addressdetails=1`) so selecting a hit
 * can auto-fill all three fields plus coordinates in one action. Mirrors the
 * astro portal's CityAutocomplete search (av-astro · geocodeSearch), but adds
 * the structured address parts the Contacts POB block stores.
 */
export interface CitySearchHit {
  /** Full human-readable place string, e.g. "Nagpur, Maharashtra, India". */
  display_name: string;
  /** Best city/town/village name (may be empty for some result types). */
  city: string;
  /** State / region (may be empty). */
  state: string;
  /** Country name (may be empty). */
  country: string;
  lat: number;
  lon: number;
}

/** GeoJSON feature shape Photon returns. */
interface PhotonFeature {
  geometry?: { coordinates?: [number, number] };
  properties?: {
    osm_key?: string; // 'place' for settlements (vs 'boundary', 'waterway', …)
    osm_value?: string; // 'city' | 'town' | 'village' | 'hamlet' | …
    name?: string;
    state?: string;
    country?: string;
    countrycode?: string; // ISO-2, upper-case
  };
}

/** Internal working row: a normalized Photon settlement + its rank inputs. */
interface RankedHit extends CitySearchHit {
  _rank: number; // SETTLEMENT_RANK value (city-like → low)
  _india: boolean;
  _ord: number; // original Photon importance order (tiebreak)
  _prefix: boolean; // name actually STARTS WITH the query (vs fuzzy match)
}

async function photonThrottle(): Promise<void> {
  const wait = lastPhotonAt + PHOTON_MIN_INTERVAL_MS - Date.now();
  if (wait > 0) await new Promise((r) => setTimeout(r, wait));
  lastPhotonAt = Date.now();
}

/** Fold accents + lowercase for prefix comparison (Brasília → brasilia). */
function fold(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // strip combining diacritics
    .toLowerCase()
    .trim();
}

/** One Photon fetch → normalized settlement rows (drops boundaries/rivers). */
async function photonFetch(query: string, bbox?: string): Promise<RankedHit[]> {
  await photonThrottle();
  const params = new URLSearchParams();
  params.set('q', query);
  params.set('limit', '15');
  params.set('lang', 'en');
  if (bbox) params.set('bbox', bbox);
  const foldedQ = fold(query);
  try {
    const res = await fetch(`${PHOTON_URL}?${params.toString()}`, {
      headers: { Accept: 'application/json' },
      // Abort a hung Photon request after 8s. On timeout the fetch rejects,
      // the catch below returns [], and the dropdown falls to "No matches"
      // instead of staying stuck on "Searching…" forever.
      signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
    });
    if (!res.ok) {
      console.warn('[geocoder] photon HTTP', res.status);
      return [];
    }
    const data = (await res.json()) as { features?: PhotonFeature[] };
    const feats = data.features ?? [];
    const rows: RankedHit[] = [];
    feats.forEach((f, i) => {
      const p = f.properties ?? {};
      if (p.osm_key !== 'place') return; // settlements only — no rivers/regions
      const rank = SETTLEMENT_RANK[p.osm_value ?? ''];
      if (rank === undefined) return;
      const coords = f.geometry?.coordinates;
      if (!coords || coords.length < 2) return;
      const [lon, lat] = coords;
      if (!isFinite(lat) || !isFinite(lon)) return;
      const name = p.name ?? '';
      const state = p.state ?? '';
      const country = p.country ?? '';
      rows.push({
        display_name: [name, state, country].filter(Boolean).join(', '),
        city: name,
        state,
        country,
        lat,
        lon,
        _rank: rank,
        _india: (p.countrycode ?? '').toUpperCase() === 'IN',
        _ord: i,
        _prefix: fold(name).startsWith(foldedQ),
      });
    });
    return rows;
  } catch (err) {
    console.warn('[geocoder] photon failed:', (err as Error).message);
    return [];
  }
}

/**
 * Live city search — returns up to `limit` candidate places so the user can
 * SELECT-from-dropdown, biased to India but never restricted to it.
 *
 * WHY PHOTON, NOT NOMINATIM: the owner needs a 3–4 letter PREFIX ("rai") to
 * return the real cities that START WITH it (Raipur, Raiganj, Raigarh) with
 * major cities first — not the tiny foreign hamlets literally named "Rai" that
 * Nominatim's whole-token `/search` returns. Photon (same OSM data, purpose-
 * built for autocomplete) does prefix matching; Nominatim structurally cannot.
 * `geocode()` above stays on Nominatim for the single structured save lookup.
 *
 * RANKING (owner's ask: real cities, India-biased, major-cities-first):
 *   1. query an India bounding box first → its importance ranking floats major
 *      Indian cities to the top;
 *   2. if that yields fewer than `limit` settlements (short/foreign queries),
 *      top up with a global (un-boxed) query so clients abroad still resolve —
 *      bias, not exclusion;
 *   3. de-dupe by coordinates, then sort by (city>town>village tier, with a
 *      one-tier India boost) → true prefix-match → original importance.
 */
export async function geocodeSearch(query: string, limit = 6): Promise<CitySearchHit[]> {
  const q = (query || '').trim();
  if (q.length < 2) return [];

  let rows = await photonFetch(q, INDIA_BBOX);
  // Top up with a global pass when the India-boxed set is thin (foreign cities,
  // or rare prefixes) so non-Indian clients still resolve.
  if (rows.length < limit) {
    rows = rows.concat(await photonFetch(q));
  }

  // De-dupe near-identical rows (Nominatim/Photon repeat "Rai Valley…") by
  // rounded coordinates.
  const seen = new Set<string>();
  const deduped = rows.filter((r) => {
    const key = `${r.lat.toFixed(3)},${r.lon.toFixed(3)}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  // Composite rank: settlement tier (×2) + India boost (one tier). A major
  // CITY outranks any village regardless of country; within a tier India wins;
  // true prefix matches beat fuzzy ones; Photon's own order breaks final ties.
  deduped.sort((a, b) => {
    const ea = a._rank * 2 + (a._india ? 0 : 1);
    const eb = b._rank * 2 + (b._india ? 0 : 1);
    if (ea !== eb) return ea - eb;
    if (a._prefix !== b._prefix) return a._prefix ? -1 : 1;
    return a._ord - b._ord;
  });

  return deduped.slice(0, limit).map(
    ({ display_name, city, state, country, lat, lon }): CitySearchHit => ({
      display_name,
      city,
      state,
      country,
      lat,
      lon,
    }),
  );
}

/** ISO-2 codes for the handful of countries where restricting helps accuracy. */
const COUNTRY_CODE_HINTS: Record<string, string> = {
  india: 'in',
  'united states': 'us',
  'united kingdom': 'gb',
  'united arab emirates': 'ae',
  canada: 'ca',
  australia: 'au',
  singapore: 'sg',
  nepal: 'np',
  'sri lanka': 'lk',
};

/** Best-effort ISO-2 hint for a country name (undefined when unknown). */
export function countryCodeHint(country: string | undefined): string | undefined {
  return COUNTRY_CODE_HINTS[(country || '').trim().toLowerCase()];
}
